// src/components/Holiday/EditHolidayDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  createHoliday,
  updateHoliday,
  Holiday,
  CreateHolidayRequest,
  UpdateHolidayRequest,
  HolidayApiError,
} from '../../services/holidayApi';

interface EditHolidayDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  holiday?: Holiday | null; // null for create mode, Holiday for edit mode
}

const EditHolidayDialog: React.FC<EditHolidayDialogProps> = ({
  open,
  onClose,
  onSuccess,
  holiday,
}) => {
  const isEditMode = !!holiday;
  
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [holidayDate, setHolidayDate] = useState<string>('');
  const [holidayName, setHolidayName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Initialize form with holiday data (for edit) or defaults (for create)
  useEffect(() => {
    if (open) {
      if (isEditMode && holiday) {
        setYear(holiday.year);
        setHolidayDate(holiday.holidayDate);
        setHolidayName(holiday.holidayName);
        setDescription(holiday.description || '');
      } else {
        // Create mode - set defaults
        const currentYear = new Date().getFullYear();
        setYear(currentYear);
        setHolidayDate('');
        setHolidayName('');
        setDescription('');
      }
      setError(null);
      setValidationErrors({});
    }
  }, [holiday, open, isEditMode]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Year validation
    if (!year || isNaN(year)) {
      errors.year = 'Year is required';
    } else if (year < 1900 || year > 2100) {
      errors.year = 'Year must be between 1900 and 2100';
    }

    // Date validation
    if (!holidayDate.trim()) {
      errors.holidayDate = 'Holiday date is required';
    } else {
      // Validate date format (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(holidayDate.trim())) {
        errors.holidayDate = 'Date must be in YYYY-MM-DD format';
      } else {
        // Validate that date is valid
        const date = new Date(holidayDate + 'T00:00:00');
        if (isNaN(date.getTime())) {
          errors.holidayDate = 'Invalid date';
        } else {
          // Validate that date matches the year
          const dateYear = date.getFullYear();
          if (dateYear !== year) {
            errors.holidayDate = `Date year (${dateYear}) must match the selected year (${year})`;
          }
        }
      }
    }

    // Holiday name validation
    if (!holidayName.trim()) {
      errors.holidayName = 'Holiday name is required';
    } else if (holidayName.trim().length > 255) {
      errors.holidayName = 'Holiday name must be 255 characters or less';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      if (isEditMode && holiday) {
        // Update existing holiday
        const updateData: UpdateHolidayRequest = {
          year,
          holidayDate: holidayDate.trim(),
          holidayName: holidayName.trim(),
          description: description.trim() || null,
        };

        await updateHoliday(holiday.holidayId, updateData);
      } else {
        // Create new holiday
        const createData: CreateHolidayRequest = {
          year,
          holidayDate: holidayDate.trim(),
          holidayName: holidayName.trim(),
          description: description.trim() || null,
          isAutoAdded: false,
        };

        await createHoliday(createData);
      }
      
      onSuccess();
    } catch (err) {
      const apiError = err as HolidayApiError;
      console.error('EditHolidayDialog: Error saving holiday:', err);
      
      // Provide more helpful error messages
      let errorMessage = apiError.message || 'Failed to save holiday';
      
      if (apiError.code === 'NETWORK_ERROR') {
        errorMessage = apiError.message || 'Network error: Unable to connect to the backend server. Please check your connection and try again.';
      } else if (apiError.code === 'NOT_FOUND') {
        errorMessage = `Holiday not found. The holiday may have been deleted.`;
      } else if (apiError.code === 'AUTH_REQUIRED') {
        errorMessage = 'Authentication required. Please refresh the page and try again.';
      } else if (apiError.code === 'DUPLICATE_HOLIDAY') {
        errorMessage = 'A holiday with the same year and date already exists. Please choose a different date.';
      } else if (apiError.code === 'VALIDATION_ERROR') {
        errorMessage = apiError.message || 'Validation failed. Please check your input.';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setValidationErrors({});
      onClose();
    }
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (dateStr: string): string => {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Otherwise, try to parse and format
    try {
      const date = new Date(dateStr + 'T00:00:00');
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Holiday' : 'Create Holiday'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Year"
            type="number"
            value={year || ''}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                setYear(value);
                setValidationErrors({ ...validationErrors, year: '' });
              }
            }}
            required
            fullWidth
            autoFocus={!isEditMode}
            disabled={isSubmitting}
            error={!!validationErrors.year}
            helperText={validationErrors.year || 'Year (1900-2100)'}
            inputProps={{ min: 1900, max: 2100 }}
          />

          <TextField
            label="Holiday Date"
            type="date"
            value={formatDateForInput(holidayDate)}
            onChange={(e) => {
              setHolidayDate(e.target.value);
              setValidationErrors({ ...validationErrors, holidayDate: '' });
            }}
            required
            fullWidth
            disabled={isSubmitting}
            error={!!validationErrors.holidayDate}
            helperText={validationErrors.holidayDate || 'Select the holiday date'}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Holiday Name"
            value={holidayName}
            onChange={(e) => {
              setHolidayName(e.target.value);
              setValidationErrors({ ...validationErrors, holidayName: '' });
            }}
            required
            fullWidth
            disabled={isSubmitting}
            error={!!validationErrors.holidayName}
            helperText={validationErrors.holidayName || 'Name of the holiday (e.g., New Year\'s Day)'}
            inputProps={{ maxLength: 255 }}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={isSubmitting}
            helperText="Optional description of the holiday"
          />

          {isEditMode && holiday && holiday.isAutoAdded && (
            <FormControlLabel
              control={<Checkbox checked={true} disabled />}
              label="Auto-Added Holiday (Standard holiday automatically created)"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !year || !holidayDate.trim() || !holidayName.trim()}
        >
          {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Holiday' : 'Create Holiday')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditHolidayDialog;

